import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Spin,
  Typography,
  message,
  notification,
} from "antd";
import { useTranslation } from "react-i18next";

import { apiClient } from "../../../services/api/apiClient";

const { Text, Paragraph } = Typography;

type MoveReason = "mistake" | "transfer" | "cession";

interface FarmOption {
  id: number;
  name: string;
  producer_id: number | null;
  producer_name: string | null;
  town_name: string | null;
  province_name: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onMoved: (result: {
    new_farm_id: number;
    new_farm_name: string;
    source_farm_deactivated: boolean;
  }) => void;
  plot: {
    id: number;
    name: string | null;
  };
  sourceFarm: {
    id: number;
    name: string;
    producer_id: number | null;
    producer_name: string | null;
  };
}

// Ventana pequeña de debounce para el search remoto — Refine no está
// en scope aquí (el modal está fuera del árbol de Refine para poder
// abrirse desde una tabla anidada), así que usamos apiClient directo.
const SEARCH_DEBOUNCE_MS = 250;

export const MovePlotRealModal = ({
  open,
  onClose,
  onMoved,
  plot,
  sourceFarm,
}: Props) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [options, setOptions] = useState<FarmOption[]>([]);
  const [selectedFarm, setSelectedFarm] = useState<FarmOption | null>(null);
  const [reason, setReason] = useState<MoveReason>("mistake");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset al abrir/cerrar para no arrastrar estado del intento anterior.
  useEffect(() => {
    if (open) {
      setSearch("");
      setOptions([]);
      setSelectedFarm(null);
      setReason("mistake");
      setNote("");
    }
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = search.trim();
    if (q.length < 2) {
      setOptions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        // FarmViewSet expone SearchFilter con `search_fields = ['name',
        // 'producer__name', 'producer__code']` (OR entre campos) y la
        // `RefineCompatiblePagination` devuelve `{ data: [...], total }`
        // usando `page_size` (snake_case) como query param. Nos saltamos
        // el dataProvider de Refine porque el modal vive fuera del árbol
        // de recursos, así que hablamos directo con el backend.
        const resp = await apiClient.get("/v1/farms/", {
          params: { search: q, page_size: 20 },
        });
        const payload = resp?.data;
        const rows: any[] = Array.isArray(payload)
          ? payload
          : (payload?.data ?? payload?.results ?? []);
        setOptions(
          rows
            .filter((f) => f.id !== sourceFarm.id)
            .map((f) => ({
              id: f.id,
              name: f.name,
              // `FarmListSerializer` expone `producer` como PK plana
              // (ModelSerializer FK → int) y `producer_name` como
              // `source='producer.name'`. Los aliases con `?.` cubren
              // por si algún day mañana pasa a serializer anidado.
              producer_id:
                f.producer_id ??
                (typeof f.producer === "number" ? f.producer : f.producer?.id) ??
                null,
              producer_name: f.producer_name ?? f.producer?.name ?? null,
              town_name: f.town_name ?? f.town?.name ?? null,
              province_name: f.province_name ?? f.province?.name ?? null,
            })),
        );
      } catch {
        setOptions([]);
      } finally {
        setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
  }, [search, open, sourceFarm.id]);

  const differentProducer = useMemo(() => {
    if (!selectedFarm) return false;
    if (sourceFarm.producer_id == null || selectedFarm.producer_id == null) {
      return false;
    }
    return selectedFarm.producer_id !== sourceFarm.producer_id;
  }, [selectedFarm, sourceFarm.producer_id]);

  const submit = async () => {
    if (!selectedFarm) {
      message.warning(
        t(
          "farms.move_plot.err_no_target",
          "Selecciona la finca destino.",
        ),
      );
      return;
    }
    setSubmitting(true);
    try {
      const resp = await apiClient.post(`/plot-reals/${plot.id}/move/`, {
        target_farm_id: selectedFarm.id,
        reason,
        note: note.trim() || undefined,
      });
      const newFarmName: string =
        resp.data?.new_farm_name || selectedFarm.name;
      const deactivated: boolean = !!resp.data?.source_farm_deactivated;

      // Toast corto + notification persistente. El toast desaparece
      // en 3s (por si el admin ya está tabulando otra acción); la
      // notification queda visible como recibo del traslado, con la
      // finca destino y un aviso extra si la origen se desactivó.
      message.success(
        t(
          "farms.move_plot.success",
          "Parcela trasladada a {{name}}",
          { name: newFarmName },
        ),
      );
      notification.success({
        message: t(
          "farms.move_plot.success_title",
          "Parcela trasladada",
        ),
        description: (
          <>
            <div>
              {t(
                "farms.move_plot.success_desc",
                "«{{plot}}» ahora pertenece a la finca {{farm}}.",
                {
                  plot: plot.name || `#${plot.id}`,
                  farm: newFarmName,
                },
              )}
            </div>
            {deactivated && (
              <div style={{ marginTop: 8, color: "#faad14" }}>
                {t(
                  "farms.move_plot.success_deactivated_hint",
                  "La finca origen se ha desactivado por quedar sin parcelas canónicas activas.",
                )}
              </div>
            )}
          </>
        ),
        placement: "topRight",
        duration: deactivated ? 8 : 5,
      });

      onMoved({
        new_farm_id: resp.data?.new_farm_id ?? selectedFarm.id,
        new_farm_name: newFarmName,
        source_farm_deactivated: deactivated,
      });
      onClose();
    } catch (err: any) {
      message.error(
        err?.response?.data?.detail ||
          err?.message ||
          t(
            "farms.move_plot.err_generic",
            "No se pudo trasladar la parcela.",
          ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={t("farms.move_plot.title", "Trasladar parcela canónica")}
      open={open}
      onCancel={onClose}
      onOk={submit}
      confirmLoading={submitting}
      okText={t("farms.move_plot.ok", "Trasladar")}
      cancelText={t("common.cancel", "Cancelar")}
      okButtonProps={{ disabled: !selectedFarm || submitting }}
      destroyOnClose
      width={560}
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <div>
          <Text type="secondary">
            {t("farms.move_plot.plot_label", "Parcela")}:
          </Text>{" "}
          <Text strong>
            {plot.name || `#${plot.id}`}
          </Text>
          <br />
          <Text type="secondary">
            {t("farms.move_plot.from_label", "Origen")}:
          </Text>{" "}
          <Text>
            {sourceFarm.name}
            {sourceFarm.producer_name ? ` · ${sourceFarm.producer_name}` : ""}
          </Text>
        </div>

        <div>
          <Text strong>
            {t("farms.move_plot.target_farm", "Finca destino")}
          </Text>
          <Select
            style={{ width: "100%", marginTop: 4 }}
            showSearch
            filterOption={false}
            placeholder={t(
              "farms.move_plot.target_placeholder",
              "Busca por nombre de finca o productor…",
            )}
            notFoundContent={
              searching ? (
                <Spin size="small" />
              ) : search.trim().length < 2 ? (
                t(
                  "farms.move_plot.target_hint",
                  "Escribe al menos 2 letras",
                )
              ) : (
                t("common.no_results", "Sin resultados")
              )
            }
            onSearch={setSearch}
            onSelect={(value) => {
              const f = options.find((o) => o.id === Number(value));
              setSelectedFarm(f || null);
            }}
            options={options.map((f) => ({
              value: f.id,
              label: (
                <span>
                  <b>{f.name}</b>
                  {f.producer_name ? ` — ${f.producer_name}` : ""}
                  {f.town_name ? ` (${f.town_name})` : ""}
                </span>
              ),
            }))}
          />
          {differentProducer && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
              message={t(
                "farms.move_plot.warn_producer_change",
                "El productor de la finca destino es distinto. Sólo aplicable para cesión o traspaso.",
              )}
            />
          )}
        </div>

        <div>
          <Text strong>
            {t("farms.move_plot.reason", "Motivo del traslado")}
          </Text>
          <Radio.Group
            value={reason}
            onChange={(e) => setReason(e.target.value as MoveReason)}
            style={{ display: "flex", flexDirection: "column", marginTop: 4 }}
          >
            <Radio value="mistake">
              {t(
                "farms.move_plot.reason_mistake",
                "Equivocación al asignar",
              )}
            </Radio>
            <Radio value="transfer">
              {t("farms.move_plot.reason_transfer", "Traspaso de finca")}
            </Radio>
            <Radio value="cession">
              {t("farms.move_plot.reason_cession", "Cesión")}
            </Radio>
          </Radio.Group>
        </div>

        <div>
          <Text strong>
            {t("farms.move_plot.note", "Nota (opcional)")}
          </Text>
          <Input.TextArea
            style={{ marginTop: 4 }}
            rows={3}
            maxLength={500}
            showCount
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t(
              "farms.move_plot.note_ph",
              "Contexto adicional para el histórico…",
            )}
          />
        </div>

        <Alert
          type="info"
          showIcon
          message={t(
            "farms.move_plot.effects_title",
            "Efectos del traslado",
          )}
          description={
            <Paragraph style={{ margin: 0 }}>
              {t(
                "farms.move_plot.effects_body",
                "La parcela dejará de contar para nuevas auditorías de la finca origen y aparecerá bajo la finca destino. Las auditorías ya creadas se conservan como snapshot histórico. Si la finca origen se queda sin parcelas canónicas activas, se desactivará automáticamente.",
              )}
            </Paragraph>
          }
        />
      </Space>
    </Modal>
  );
};

export default MovePlotRealModal;
